import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Panel flotante anclado a un botón, pintado en el `body`.
 *
 * Lo comparten el menú de acciones de fila, los selectores de filtros y
 * cualquier otro desplegable: dentro de una tabla con `overflow` o de una
 * barra con `position: relative`, un panel hijo se recorta por mucho z-index
 * que lleve. Con posición fija sobre el body eso deja de pasar, a cambio de
 * tener que recolocarlo a mano mientras se hace scroll.
 *
 * @param ancho ancho del panel en píxeles (hace falta para no salirse)
 * @param alinear 'izquierda' o 'derecha' respecto al botón
 * @returns { abierto, abrir, cerrar, alternar, refBoton, refPanel, estilo }
 */
const MARGEN = 8;

const usePopoverAnclado = ({ ancho = 260, alinear = 'izquierda' } = {}) => {
  const [abierto, setAbierto] = useState(false);
  const [posicion, setPosicion] = useState(null);
  const refBoton = useRef(null);
  const refPanel = useRef(null);

  const cerrar = useCallback((devolverFoco = true) => {
    setAbierto(false);
    if (devolverFoco) refBoton.current?.focus();
  }, []);

  const abrir = useCallback(() => setAbierto(true), []);
  const alternar = useCallback(() => setAbierto((v) => !v), []);

  /**
   * Coloca el panel debajo del botón, o encima si no cabe. Si no cabe en
   * ninguno de los dos lados se queda en el que tenga más sitio y se le pone
   * un alto máximo para que scrollee él en vez de salirse de la pantalla.
   */
  const recolocar = useCallback(() => {
    const boton = refBoton.current;
    if (!boton) return;
    const caja = boton.getBoundingClientRect();
    // scrollHeight y no offsetHeight: si ya lleva un maxHeight puesto,
    // offsetHeight devuelve el recortado y el panel se quedaría encogido
    const alto = refPanel.current?.scrollHeight || 0;
    const sitioDebajo = window.innerHeight - caja.bottom - MARGEN - 6;
    const sitioEncima = caja.top - MARGEN - 6;
    const debajo = alto <= sitioDebajo || sitioDebajo >= sitioEncima;
    const disponible = debajo ? sitioDebajo : sitioEncima;
    const inicio = alinear === 'derecha' ? caja.right - ancho : caja.left;

    const siguiente = {
      top: debajo ? caja.bottom + 6 : Math.max(MARGEN, caja.top - Math.min(alto, disponible) - 6),
      left: Math.min(Math.max(MARGEN, inicio), window.innerWidth - ancho - MARGEN),
      maxHeight: Math.max(160, disponible),
    };
    // Sin esta comparación, el ResizeObserver y el alto máximo que él mismo
    // provoca se realimentan y el componente no para de repintarse
    setPosicion((previa) => (
      previa
        && previa.top === siguiente.top
        && previa.left === siguiente.left
        && previa.maxHeight === siguiente.maxHeight
        ? previa
        : siguiente
    ));
  }, [ancho, alinear]);

  useLayoutEffect(() => {
    if (!abierto) {
      setPosicion(null);
      return undefined;
    }
    recolocar();
    // El panel se mide cuando ya está montado; con contenido que cambia de alto
    // (un calendario que cambia de mes, una lista que se filtra) hay que
    // recolocarlo otra vez o se sale por abajo
    const panel = refPanel.current;
    if (!panel || typeof ResizeObserver === 'undefined') return undefined;
    const observador = new ResizeObserver(() => recolocar());
    observador.observe(panel);
    return () => observador.disconnect();
  }, [abierto, recolocar]);

  useEffect(() => {
    if (!abierto) return undefined;
    // Al desplazar, el panel sigue a su botón en vez de cerrarse. Solo se
    // cierra si el botón se ha ido de la pantalla, porque entonces ya no hay
    // nada a lo que anclarse.
    const alDesplazar = (e) => {
      if (refPanel.current?.contains(e.target)) return;
      const caja = refBoton.current?.getBoundingClientRect();
      if (!caja || caja.bottom < 0 || caja.top > window.innerHeight) {
        setAbierto(false);
        return;
      }
      recolocar();
    };
    const alPulsarFuera = (e) => {
      if (!refPanel.current?.contains(e.target) && !refBoton.current?.contains(e.target)) setAbierto(false);
    };
    const alTeclear = (e) => { if (e.key === 'Escape') cerrar(); };
    window.addEventListener('scroll', alDesplazar, true);
    window.addEventListener('resize', recolocar);
    document.addEventListener('mousedown', alPulsarFuera);
    document.addEventListener('keydown', alTeclear);
    return () => {
      window.removeEventListener('scroll', alDesplazar, true);
      window.removeEventListener('resize', recolocar);
      document.removeEventListener('mousedown', alPulsarFuera);
      document.removeEventListener('keydown', alTeclear);
    };
  }, [abierto, cerrar, recolocar]);

  return {
    abierto,
    abrir,
    cerrar,
    alternar,
    refBoton,
    refPanel,
    // Mientras no se ha medido, el panel se monta invisible: hace falta que
    // exista en el DOM para poder saber cuánto ocupa
    estilo: posicion
      ? {
        top: posicion.top,
        left: posicion.left,
        width: ancho,
        maxHeight: posicion.maxHeight,
        overflowY: 'auto',
      }
      : { top: 0, left: 0, width: ancho, visibility: 'hidden' },
  };
};

export default usePopoverAnclado;
